/**
 * Public types and shared helpers for the task-execution hook.
 */

export enum TaskPhase {
  ANALYSIS = 1,
  GENERATE_QUESTIONS = 2,
  WAITING_FOR_INPUT = 3,
  PLANNING = 4,
  APPLY_CHANGES = 5,
  VERIFICATION = 6,
  WAITING_FOR_REVIEW = 7,
  DECISION = 8,
  CREATE_PR = 9,
}

// Single source of truth for phase display metadata.
// Record<TaskPhase, ...> ensures every enum member has an entry at compile time.
export const TASK_PHASE_METADATA: Record<
  TaskPhase,
  { id: string; title: string; description: string }
> = {
  [TaskPhase.ANALYSIS]: {
    id: 'analysis',
    title: 'Analyzing',
    description: 'Analyzing repository structure',
  },
  [TaskPhase.GENERATE_QUESTIONS]: {
    id: 'questions',
    title: 'Generating Questions',
    description: 'Preparing configuration options',
  },
  [TaskPhase.WAITING_FOR_INPUT]: {
    id: 'input',
    title: 'Your Input Needed',
    description: 'Answer the configuration questions below',
  },
  [TaskPhase.PLANNING]: {
    id: 'planning',
    title: 'Planning',
    description: 'Creating transformation plan',
  },
  [TaskPhase.APPLY_CHANGES]: {
    id: 'applying',
    title: 'Applying Changes',
    description: 'Modifying repository files',
  },
  [TaskPhase.VERIFICATION]: {
    id: 'verification',
    title: 'Verifying',
    description: 'Running tests and validation',
  },
  [TaskPhase.WAITING_FOR_REVIEW]: {
    id: 'review',
    title: 'Review Required',
    description: 'Review changes and approve or request modifications',
  },
  [TaskPhase.DECISION]: {
    id: 'decision',
    title: 'Processing',
    description: 'Processing your decision',
  },
  [TaskPhase.CREATE_PR]: {
    id: 'pr',
    title: 'Creating PR',
    description: 'Creating pull request',
  },
};

export interface Question {
  id: string;
  question: string;
  questionType: 'text' | 'select' | 'boolean' | 'multiselect';
  options?: string[];
  default?: string | boolean | string[];
  helpText?: string;
  optionDescriptions?: Record<string, string>;
}

/** Raw question shape from backend (may use snake_case) */
export interface RawQuestion {
  id: string;
  question: string;
  questionType?: Question['questionType'];
  question_type?: Question['questionType'];
  options?: string[];
  default?: string | boolean;
  helpText?: string;
  help_text?: string;
  optionDescriptions?: Record<string, string>;
  option_descriptions?: Record<string, string>;
}

/** Normalize backend snake_case question fields to camelCase. */
export function normalizeQuestions(raw: RawQuestion[]): Question[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((q) => ({
    id: q.id,
    question: q.question,
    questionType: q.questionType || q.question_type || 'text',
    options: q.options,
    default: q.default,
    helpText: q.helpText || q.help_text,
    optionDescriptions: q.optionDescriptions || q.option_descriptions,
  }));
}

export interface VerificationResult {
  overall_success: boolean;
  tests_passed?: boolean;
  build_passed?: boolean;
  lint_passed?: boolean;
  errors: string[];
}

export interface TaskPreview {
  diff: string;
  diffKey?: string;
  files: string[];
  iteration: number;
}

export interface TaskWorkflowState {
  steps: Array<{
    id: string;
    title: string;
    description: string;
    order: number;
    status: string;
    weight: number;
    startTime: string | null;
    endTime: string | null;
    durationSeconds: number | null;
    error: string | null;
  }>;
  currentStep: string | null;
  totalSteps: number;
  completedSteps: number;
  percent: number;
  failedStep: string | null;
  updatedAt: string;
}

export interface TaskStatus {
  taskId: string;
  workflowId: string;
  status: string;
  phase: TaskPhase;
  iteration: number;
  questions: Question[] | null;
  // Most recent answers the user submitted, persisted server-side so the
  // question form can pre-fill after a page refresh mid-revision. Null
  // until the first submit.
  userAnswers?: Record<string, unknown> | null;
  preview: TaskPreview | null;
  verification: VerificationResult | null;
  errors: string[];
  type?: string; // Task class (e.g., "pip_to_uv") - legacy field
  taskClass?: string; // Task class identifier
  repositoryId?: string; // Repository ID for navigation
  startedAt?: string; // When task started (ISO timestamp)
  phaseStartedAt?: string; // When current phase started (ISO timestamp from backend)
  workflow?: TaskWorkflowState; // Granular step tracking from backend
  outputs?: {
    pr_url?: string;
    success?: boolean;
    [key: string]: unknown;
  };
}

export interface TaskType {
  taskClass: string;
  displayName: string;
  description: string;
  icon: string;
  category: string;
  priority: string;
}

export interface StartTaskParams {
  taskClass: string;
  repositoryId: string;
  containerId: string;
  workspacePath?: string;
  config?: Record<string, unknown>;
}

export interface UseTaskExecutionReturn {
  taskStatus: TaskStatus | null;
  loading: boolean;
  error: string | null;
  taskTypes: TaskType[];
  startTask: (params: StartTaskParams) => Promise<{ taskId: string; workflowId: string } | null>;
  submitAnswers: (answers: Record<string, unknown>) => Promise<void>;
  submitReview: (
    decision: 'approve' | 'reject' | 'request_changes',
    feedback?: string,
  ) => Promise<void>;
  reviseAnswers: () => Promise<void>;
  cancelTask: () => Promise<void>;
  retryTask: (failedTaskId: string) => Promise<{ taskId: string; workflowId: string } | null>;
  // Last answers submitted for this task in the current session — used to
  // pre-fill the question form when the user returns to revise answers.
  lastAnswers: Record<string, unknown> | undefined;
}

/** WebSocket payload for task-progress events */
export interface TaskProgressEvent {
  taskId: string;
  timestamp?: string;
  phaseStartedAt?: string;
  currentPhase?: TaskPhase;
  status?: string;
  workflow?: TaskWorkflowState;
}

/** WebSocket payload for task-questions-ready events */
export interface TaskQuestionsReadyEvent {
  taskId: string;
  questions: RawQuestion[];
}

/** WebSocket payload for task-verification-complete events */
export interface TaskVerificationCompleteEvent {
  taskId: string;
  verification: VerificationResult;
}

/** WebSocket payload for task-review-ready events */
export interface TaskReviewReadyEvent {
  taskId: string;
  diff_key?: string;
  verification?: VerificationResult;
  iteration?: number;
}

/** WebSocket payload for task-completed events */
export interface TaskCompletedEvent {
  taskId: string;
  pr_url?: string;
  workflow?: TaskWorkflowState;
}

/** WebSocket payload for task-error events */
export interface TaskErrorEvent {
  taskId: string;
  message?: string;
  error?: string;
  workflow?: TaskWorkflowState;
}

/** WebSocket payload for task-cancelled events */
export interface TaskCancelledEvent {
  taskId: string;
  workflow?: TaskWorkflowState;
}
